import { Content, List, Root, Trigger } from '@radix-ui/react-tabs';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import type { ComponentProps, CSSProperties, FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export type TabsProps = {
    readonly children: ReactNode;
    readonly defaultValue?: string;
    readonly onValueChange?: (value: string) => void;
    readonly orientation?: 'horizontal';
    readonly value?: string;
};

export type TabsListProps = {
    readonly children: ReactNode;
    readonly style?: CSSProperties;
};

export type TabsTriggerProps = {
    readonly children: ReactNode;
    readonly className?: string;
    readonly disabled?: boolean;
    readonly value: string;
};

export type TabsContentProps = {
    readonly children: ReactNode;
    readonly value: string;
};

export type TabItem = {
    readonly label: ReactNode;
    readonly value: string;
};

export type AnimatedTabs = Omit<TabsProps, 'children'> & {
    readonly children?: ReactNode;
    readonly style?: CSSProperties;
    readonly tabs: readonly TabItem[];
};

const tabsTriggerClasses = clsx(
    'flex items-center justify-center relative z-[2]',
    'text-sm font-normal leading-[1.25rem] text-gray-700',
    'py-xs px-l rounded-md cursor-pointer select-none',
    'transition-all duration-200 ease-in-out',
    'focus-visible:outline-1 focus-visible:outline-orange-800 focus-visible:outline-offset-[1px]',
    'data-[disabled]:text-gray-400 data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
    'data-[state=active]:text-black-700 data-[state=active]:z-[2]',
);

const TabsTriggerComponent: FunctionComponent<ComponentProps<typeof Trigger>> = ({ className, ...props }) => (
    <Trigger
        className={clsx(tabsTriggerClasses, className)}
        style={{
            alignItems: 'center',
            all: 'unset',
            borderRadius: 'var(--spacing-m)',
            color: 'inherit',
            cursor: 'pointer',
            display: 'flex',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            justifyContent: 'center',
            lineHeight: 'inherit',
            padding: 'var(--spacing-xs) var(--spacing-l)',
            position: 'relative',
            transition: 'all 0.2s ease-in-out',
            userSelect: 'none',
            zIndex: 2,
        }}
        {...props}
    />
);

export const Tabs: FunctionComponent<TabsProps> = ({
    children,
    defaultValue,
    onValueChange,
    orientation = 'horizontal',
    value,
    ...props
}) => {
    return (
        <Root
            defaultValue={defaultValue}
            orientation={orientation}
            value={value}
            onValueChange={onValueChange}
            {...props}
            style={{ position: 'relative' }}
        >
            {children}
        </Root>
    );
};

export const TabsList: FunctionComponent<TabsListProps> = ({ children, ...props }) => {
    return (
        <List className="flex bg-gray-100 rounded-xl p-xs relative font-sans" {...props}>
            {children}
        </List>
    );
};

export const TabsTrigger: FunctionComponent<TabsTriggerProps> = ({ children, disabled, value, ...props }) => {
    return (
        <TabsTriggerComponent disabled={disabled} value={value} {...props}>
            {children}
        </TabsTriggerComponent>
    );
};

export const TabsContent: FunctionComponent<TabsContentProps> = ({ children, value, ...props }) => {
    return (
        <Content
            className={clsx(
                'text-gray-900 text-base leading-[1.5rem] outline-none p-m',
                'focus-visible:rounded-xs focus-visible:outline-2 focus-visible:outline-gray-600 focus-visible:outline-offset-2',
                'data-[state=inactive]:hidden',
            )}
            value={value}
            {...props}
        >
            {children}
        </Content>
    );
};

export const AnimatedTabs: FunctionComponent<AnimatedTabs> = ({
    children,
    defaultValue,
    onValueChange,
    orientation = 'horizontal',
    tabs,
    value,
    ...props
}) => {
    const [activeTab, setActiveTab] = useState(value ?? defaultValue ?? tabs[0]?.value ?? '');
    const activeTabRef = useRef<Record<string, HTMLButtonElement | null>>({});
    const layoutIdRef = useRef<string>(Math.random().toString(36).substring(2, 15));
    useEffect(() => {
        if (value !== undefined && value !== activeTab) {
            setActiveTab(value);
        }
    }, [value, activeTab]);

    const handleValueChange = (newValue: string): void => {
        setActiveTab(newValue);
        onValueChange?.(newValue);
    };

    return (
        <Tabs {...props} orientation={orientation} value={activeTab} onValueChange={handleValueChange}>
            <TabsList>
                {tabs.map((tab) => (
                    <TabsTriggerComponent
                        key={tab.value}
                        ref={(ref) => {
                            activeTabRef.current[tab.value] = ref;
                        }}
                        value={tab.value}
                    >
                        {tab.label}
                        {activeTab === tab.value && (
                            <motion.span
                                animate={{}}
                                className="absolute inset-0 bg-white-200 rounded-md shadow-sm -z-[1]"
                                initial={false}
                                layoutId={layoutIdRef.current}
                                tabIndex={-1}
                                transition={{
                                    duration: 0.2,
                                }}
                            />
                        )}
                    </TabsTriggerComponent>
                ))}
            </TabsList>
            <TabsList aria-hidden="true" style={{ position: 'absolute', top: 0 }}>
                {tabs.map((tab) => (
                    <TabsTriggerComponent
                        key={tab.value}
                        ref={(ref) => {
                            activeTabRef.current[tab.value] = ref;
                        }}
                        value={tab.value}
                    >
                        {tab.label}
                    </TabsTriggerComponent>
                ))}
            </TabsList>
            {children}
        </Tabs>
    );
};
